import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, ErrorMessage, Field, Loading, ModuleHeader, SearchSelect, readImageAsDataUrl, useAsync, useToast } from '../components/ui';
import { api } from '../lib/api';
import { formatDuration } from '../lib/format';
import { IconCamera, IconPlus } from '../components/icons';

/**
 * Meldeformular (Kanal "Weboberfläche"). Kritikalität, Kategorie und Kanal
 * stammen vollständig aus den administrierbaren Katalogen.
 */
export function TaskNewPage() {
  const navigate = useNavigate();
  const { notify } = useToast();
  const catalogs = useAsync(() => api.get('/api/catalogs'), []);
  const properties = useAsync(() => api.get('/api/properties'), []);

  const [form, setForm] = useState({
    title: '',
    description: '',
    propertyId: '',
    locationDetail: '',
    categoryId: '',
    criticalityId: '',
    channelId: '',
    estimatedMinutes: '',
    scheduledStart: '',
    reporterFirstName: '',
    reporterName: '',
    reporterPhone: '',
    reporterEmail: '',
    reporterContact: '',
  });
  const [photos, setPhotos] = useState<{ filename: string; dataUrl: string }[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }));

  if (catalogs.loading || properties.loading) return <Loading />;

  const criticality = (catalogs.data?.criticalityLevels ?? []).find(
    (level: any) => String(level.id) === form.criticalityId,
  );
  const selectedChannel = (catalogs.data?.channels ?? []).find((channel: any) => String(channel.id) === form.channelId);
  const contactRequested = !!selectedChannel?.requires_reporter_contact;
  const contactComplete =
    form.reporterName.trim().length > 0 &&
    form.reporterFirstName.trim().length > 0 &&
    (form.reporterPhone.trim().length > 0 || form.reporterEmail.trim().length > 0);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post('/api/tasks', {
        title: form.title,
        description: form.description || undefined,
        propertyId: form.propertyId ? Number(form.propertyId) : undefined,
        locationDetail: form.locationDetail || undefined,
        categoryId: form.categoryId ? Number(form.categoryId) : undefined,
        criticalityId: form.criticalityId ? Number(form.criticalityId) : undefined,
        channelId: form.channelId ? Number(form.channelId) : undefined,
        estimatedMinutes: form.estimatedMinutes ? Number(form.estimatedMinutes) : undefined,
        scheduledStart: form.scheduledStart ? form.scheduledStart.replace('T', ' ') + ':00' : undefined,
        reporterFirstName: form.reporterFirstName || undefined,
        reporterName: form.reporterName || undefined,
        reporterPhone: form.reporterPhone || undefined,
        reporterEmail: form.reporterEmail || undefined,
        reporterContact: form.reporterContact || undefined,
        attachments: photos.map((photo) => ({ filename: photo.filename, mimeType: 'image/jpeg', dataUrl: photo.dataUrl })),
      });
      notify(`Meldung ${created.task_no} wurde angelegt.`, 'success');
      navigate(`/tasks/${created.id}`);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack measure">
      <ModuleHeader
        icon={<IconPlus width={22} height={22} />}
        title="Neue Meldung erfassen"
        subtitle={
          <>
            Jede Meldung erzeugt einen einheitlichen Auftrag – unabhängig davon, über welchen Kanal sie
            eingegangen ist. Die SLA-Fristen werden automatisch aus der gewählten Kritikalitätsstufe berechnet.
          </>
        }
        pill="Erfassung"
        tone="success"
      />

      <ErrorMessage error={error} />

      <Card title="Angaben zur Meldung">
        <div className="stack">
          <Field label="Titel" required hint="Kurz und aussagekräftig, z. B. „Heizung im Treppenhaus ohne Funktion“.">
            <input value={form.title} onChange={(event) => set('title', event.target.value)} autoFocus />
          </Field>

          <Field label="Beschreibung">
            <textarea
              value={form.description}
              onChange={(event) => set('description', event.target.value)}
              placeholder="Was ist passiert? Seit wann? Wer ist betroffen?"
            />
          </Field>

          <div className="form-grid">
            <Field label="Objekt / Liegenschaft">
              <SearchSelect
                value={String(form.propertyId ?? '')}
                onChange={(wert) => set('propertyId', wert)}
                options={(properties.data ?? []).map((property: any) => ({ value: String(property.id), label: `${property.name} (${property.city})` }))}
              />
            </Field>

            <Field label="Ort im Objekt" hint="z. B. Treppenhaus 2. OG, Tiefgarage Bereich D">
              <input value={form.locationDetail} onChange={(event) => set('locationDetail', event.target.value)} />
            </Field>

            <Field label="Kategorie / Gewerk">
              <SearchSelect
                value={String(form.categoryId ?? '')}
                onChange={(wert) => set('categoryId', wert)}
                options={(catalogs.data?.categories ?? []).map((category: any) => ({ value: String(category.id), label: String(category.name) }))}
              />
            </Field>

            <Field
              label="Kritikalität"
              hint={
                criticality
                  ? `Reaktion in ${formatDuration(criticality.response_minutes)}, Erledigung in ${formatDuration(criticality.resolution_minutes)}`
                  : 'Bestimmt Reaktions- und Erledigungsfrist.'
              }
            >
              <SearchSelect
                value={String(form.criticalityId ?? '')}
                onChange={(wert) => set('criticalityId', wert)}
                options={(catalogs.data?.criticalityLevels ?? []).map((level: any) => ({ value: String(level.id), label: `${level.level}. ${level.name}` }))}
              />
            </Field>

            <Field label="Meldekanal">
              <SearchSelect
                value={String(form.channelId ?? '')}
                onChange={(wert) => set('channelId', wert)}
                options={(catalogs.data?.channels ?? []).map((channel: any) => ({ value: String(channel.id), label: String(channel.name) }))}
              />
            </Field>

            <Field label="Geplante Dauer (Minuten)">
              <input
                type="number"
                min="0"
                step="5"
                value={form.estimatedMinutes}
                onChange={(event) => set('estimatedMinutes', event.target.value)}
              />
            </Field>

            <Field label="Terminvorschlag">
              <input
                type="datetime-local"
                value={form.scheduledStart}
                onChange={(event) => set('scheduledStart', event.target.value)}
              />
            </Field>
          </div>

          {criticality?.example && (
            <div className="alert info">
              <strong>{criticality.name}:</strong> {criticality.example}
              {criticality.escalation_note ? ` · Eskalation: ${criticality.escalation_note}` : ''}
            </div>
          )}

          <Field label="Fotos" hint="Optional – z. B. zur Dokumentation des Schadens.">
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (file) {
                  const dataUrl = await readImageAsDataUrl(file);
                  setPhotos((current) => [...current, { filename: file.name || `foto-${Date.now()}.jpg`, dataUrl }]);
                }
                event.target.value = '';
              }}
            />
            {photos.length > 0 && (
              <div className="photo-grid" style={{ marginBottom: 10 }}>
                {photos.map((photo, index) => (
                  <img
                    key={index}
                    src={photo.dataUrl}
                    alt=""
                    onClick={() => setPhotos((current) => current.filter((_, i) => i !== index))}
                  />
                ))}
              </div>
            )}
            <button type="button" className="btn" onClick={() => fileInput.current?.click()}>
              <IconCamera width={16} height={16} /> Foto hinzufügen
            </button>
          </Field>

          {contactRequested && (
            <>
              <div className="form-grid">
                <Field label="Vorname (Melder)">
                  <input
                    value={form.reporterFirstName}
                    onChange={(event) => set('reporterFirstName', event.target.value)}
                  />
                </Field>
                <Field label="Name (Melder)">
                  <input value={form.reporterName} onChange={(event) => set('reporterName', event.target.value)} />
                </Field>
                <Field label="Telefon (Melder)">
                  <input value={form.reporterPhone} onChange={(event) => set('reporterPhone', event.target.value)} />
                </Field>
                <Field label="E-Mail (Melder)">
                  <input value={form.reporterEmail} onChange={(event) => set('reporterEmail', event.target.value)} />
                </Field>
              </div>
              <Field label="Information zum Kontakt" hint="z. B. am besten abends erreichbar">
                <input value={form.reporterContact} onChange={(event) => set('reporterContact', event.target.value)} />
              </Field>
              {!contactComplete && (
                <div className="alert warning">
                  Für den gewählten Meldekanal sollten Name, Vorname und Telefonnummer oder E-Mail-Adresse des
                  Melders erfasst werden – die Meldung lässt sich aber auch ohne diese Angaben anlegen.
                </div>
              )}
            </>
          )}

          <div className="row end">
            <button className="btn" onClick={() => navigate(-1)}>
              Abbrechen
            </button>
            <button className="btn primary" onClick={submit} disabled={busy || form.title.trim().length < 3}>
              {busy ? 'Wird angelegt …' : 'Meldung anlegen'}
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}
